import React from "react";
import {
  Drawer,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Grid,
  Chip,
  Icon,
  alpha,
  useTheme,
  IconButton,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import { styled } from "@mui/material/styles";
import { BillOfMaterial } from "./types";

// Custom theme colors to match the form
const THEME_COLORS = {
  primary: '#0891B2',  // Cyan-600
  primaryHover: '#0E7490', // Cyan-700
  secondary: '#64748B', // Slate-500
  danger: '#DC2626', // Red-600
  dangerHover: '#B91C1C', // Red-700
  success: '#059669', // Emerald-600
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B', // Slate-800
    secondary: '#64748B', // Slate-500
    light: '#94A3B8', // Slate-400
  }
};

interface BOMDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  bom: BillOfMaterial;
}

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: THEME_COLORS.background.card,
  borderRadius: 12,
  border: `1px solid ${THEME_COLORS.border.light}`,
  marginBottom: theme.spacing(3),
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
}));

const SectionTitle = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: THEME_COLORS.primary,
  fontFamily: 'Montserrat',
  fontWeight: 600,
  marginBottom: 16,
  '& .MuiIcon-root': {
    color: THEME_COLORS.primary,
    fontSize: 20,
  },
});

const EmptyState = ({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        textAlign: "center",
        bgcolor: alpha(THEME_COLORS.primary, 0.04),
        borderRadius: 2,
      }}
    >
      <Icon
        sx={{
          fontSize: 48,
          color: THEME_COLORS.primary,
          opacity: 0.5,
          mb: 2,
        }}
      >
        {icon}
      </Icon>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          color: THEME_COLORS.text.primary,
          fontFamily: 'Montserrat',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ color: THEME_COLORS.text.secondary }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export const BOMDetailsDrawer: React.FC<BOMDetailsDrawerProps> = ({
  open,
  onClose,
  bom,
}) => {
  const theme = useTheme();

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateTotalTime = () => {
    return bom.preparation_steps.reduce((total, step) => {
      const prepTime = step.preparation_time || 0;
      const cookTime = step.cooking_time || 0;
      return total + prepTime + cookTime;
    }, 0);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "600px",
          maxWidth: "100%",
          bgcolor: THEME_COLORS.background.light,
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${THEME_COLORS.border.light}`,
            bgcolor: THEME_COLORS.background.main,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  color: THEME_COLORS.text.primary,
                  mb: 0.5,
                }}
              >
                {bom.name}
              </Typography>
              <Typography 
                variant="body2"
                sx={{ color: THEME_COLORS.text.secondary }}
              >
                Bill of Materials Details
              </Typography>
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{ 
                color: THEME_COLORS.text.secondary,
                '&:hover': {
                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                }
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
          {/* Basic Info */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>info</Icon>
                Basic Information
              </SectionTitle>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: THEME_COLORS.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    {bom.description || "No description provided"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Shelf Life
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: THEME_COLORS.text.primary,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Icon sx={{ color: THEME_COLORS.primary, fontSize: 20 }}>schedule</Icon>
                    {bom.shelf_life_duration} days
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={bom.is_active ? "Active" : "Inactive"}
                    sx={{
                      bgcolor: bom.is_active 
                        ? alpha(THEME_COLORS.success, 0.1)
                        : alpha(THEME_COLORS.text.secondary, 0.1),
                      color: bom.is_active 
                        ? THEME_COLORS.success
                        : THEME_COLORS.text.secondary,
                      fontWeight: 600,
                      borderRadius: 1,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Total Time Required
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: THEME_COLORS.text.primary,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Icon sx={{ color: THEME_COLORS.primary, fontSize: 20 }}>timer</Icon>
                    {calculateTotalTime()} minutes
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Ingredients */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>restaurant</Icon>
                Ingredients
              </SectionTitle>

              {bom.ingredients.length > 0 ? (
                <List 
                  disablePadding 
                  sx={{ 
                    bgcolor: THEME_COLORS.background.light,
                    borderRadius: 2,
                    border: `1px solid ${THEME_COLORS.border.light}`,
                  }}
                >
                  {bom.ingredients.map((ingredient, index) => (
                    <ListItem
                      key={ingredient.ingredient_id}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: index < bom.ingredients.length - 1 
                          ? `1px solid ${THEME_COLORS.border.light}`
                          : 'none',
                      }}
                    >
                      <ListItemIcon>
                        <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ 
                            color: THEME_COLORS.text.primary,
                            fontWeight: 500,
                          }}>
                            {ingredient.brand_name}
                          </Typography>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            sx={{ color: THEME_COLORS.text.secondary }}
                          >
                            {ingredient.quantity} {ingredient.unit}
                          </Typography>
                        }
                      />
                      {ingredient.notes && (
                        <Chip
                          size="small"
                          icon={<Icon fontSize="small">notes</Icon>}
                          label="Has notes"
                          sx={{
                            bgcolor: alpha(THEME_COLORS.primary, 0.1),
                            color: THEME_COLORS.primary,
                            borderRadius: 1,
                          }}
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <EmptyState
                  icon="restaurant"
                  title="No Ingredients"
                  description="No ingredients have been added to this recipe yet"
                />
              )}
            </CardContent>
          </StyledCard>

          {/* Processed Items */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>inventory</Icon>
                Processed Items
              </SectionTitle>

              {bom.processed_items.length > 0 ? (
                <List 
                  disablePadding
                  sx={{ 
                    bgcolor: THEME_COLORS.background.light,
                    borderRadius: 2,
                    border: `1px solid ${THEME_COLORS.border.light}`,
                  }}
                >
                  {bom.processed_items.map((item, index) => (
                    <ListItem
                      key={item.processed_item_id}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: index < bom.processed_items.length - 1 
                          ? `1px solid ${THEME_COLORS.border.light}`
                          : 'none',
                      }}
                    >
                      <ListItemIcon>
                        <Icon sx={{ color: THEME_COLORS.primary }}>inventory</Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ 
                            color: THEME_COLORS.text.primary,
                            fontWeight: 500,
                          }}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            sx={{ color: THEME_COLORS.text.secondary }}
                          >
                            {item.consumbtion_quantity} {item.consumbtion_unit}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <EmptyState
                  icon="inventory"
                  title="No Processed Items"
                  description="No processed items have been added to this recipe"
                />
              )}
            </CardContent>
          </StyledCard>

          {/* Preparation Steps */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>list_alt</Icon>
                Preparation Steps
              </SectionTitle>

              {bom.preparation_steps.length > 0 ? (
                <Box sx={{ pl: 2 }}>
                  {bom.preparation_steps.map((step, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 3,
                        mb: index < bom.preparation_steps.length - 1 ? 4 : 0,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: '20px',
                          top: '40px',
                          bottom: index < bom.preparation_steps.length - 1 ? '-32px' : '0',
                          width: '2px',
                          bgcolor: THEME_COLORS.border.light,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: THEME_COLORS.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontFamily: 'Montserrat',
                          }}
                        >
                          {step.step_number}
                        </Typography>
                      </Box>
                      <Card
                        sx={{
                          flex: 1,
                          bgcolor: THEME_COLORS.background.light,
                          border: `1px solid ${THEME_COLORS.border.light}`,
                          borderRadius: 3,
                          boxShadow: 'none',
                          p: 3,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: THEME_COLORS.text.primary,
                            fontWeight: 500,
                            mb: 3,
                          }}
                        >
                          {step.instruction}
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: THEME_COLORS.text.secondary, mb: 0.5 }}
                            >
                              Preparation Time
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Icon
                                sx={{
                                  color: THEME_COLORS.primary,
                                  fontSize: 20,
                                }}
                              >
                                timer
                              </Icon>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: THEME_COLORS.text.primary,
                                  fontWeight: 500,
                                }}
                              >
                                {step.preparation_time} {step.preparation_time_unit}
                              </Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: THEME_COLORS.text.secondary, mb: 0.5 }}
                            >
                              Cooking Time
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Icon
                                sx={{
                                  color: THEME_COLORS.primary,
                                  fontSize: 20,
                                }}
                              >
                                microwave
                              </Icon>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: THEME_COLORS.text.primary,
                                  fontWeight: 500,
                                }}
                              >
                                {step.cooking_time} {step.cooking_time_unit}
                              </Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: THEME_COLORS.text.secondary, mb: 0.5 }}
                            >
                              Temperature
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Icon
                                sx={{
                                  color: THEME_COLORS.primary,
                                  fontSize: 20,
                                }}
                              >
                                thermostat
                              </Icon>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: THEME_COLORS.text.primary,
                                  fontWeight: 500,
                                }}
                              >
                                {step.temperature}°{step.temperature_unit}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <EmptyState
                  icon="list_alt"
                  title="No Preparation Steps"
                  description="No preparation steps have been added to this recipe"
                />
              )}
            </CardContent>
          </StyledCard>

          {/* Activity Logs */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>history</Icon>
                Activity History
              </SectionTitle>

              {bom.activity_logs.length > 0 ? (
                <List 
                  disablePadding
                  sx={{ 
                    bgcolor: THEME_COLORS.background.light,
                    borderRadius: 2,
                    border: `1px solid ${THEME_COLORS.border.light}`,
                  }}
                >
                  {bom.activity_logs.map((log, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: index < bom.activity_logs.length - 1 
                          ? `1px solid ${THEME_COLORS.border.light}`
                          : 'none',
                      }}
                    >
                      <ListItemIcon>
                        <Icon sx={{ 
                          color: THEME_COLORS.primary,
                        }}>
                          {log.action === "create" ? "add_circle" : "edit"}
                        </Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ 
                            color: THEME_COLORS.text.primary,
                            fontWeight: 500,
                          }}>
                            {log.action.charAt(0).toUpperCase() + log.action.slice(1)}
                          </Typography>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            sx={{ color: THEME_COLORS.text.secondary }}
                          >
                            {formatDate(log.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <EmptyState
                  icon="history"
                  title="No Activity History"
                  description="No activity has been recorded for this recipe"
                />
              )}
            </CardContent>
          </StyledCard>
        </Box>
      </Box>
    </Drawer>
  );
};
